// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "./AdminHome.css";
import "./AdminDashboard.css";
import { useNavPinned } from "./useNavPinned";
import AdminTopNav from "./AdminTopNav";
import { API_ENDPOINTS } from "../config/api";
import { clearAuthSession } from "../utils/auth";


function formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getInitials(name) {
    if (!name) return "?";
    return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function UserAvatar({ user }) {
    if (user.profilePic || user.avatar) {
        return (
            <img
                src={user.profilePic || user.avatar}
                alt={user.name || "User"}
                className="adash-avatar-img"
            />
        );
    }
    return (
        <div className="adash-avatar-initials">
            {getInitials(user.name || user.email)}
        </div>
    );
}

export default function AdminDashboard() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [tab, setTab] = useState("all");
    const [navPinned, setNavPinned] = useNavPinned();

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const token = sessionStorage.getItem("token");
            if (!token || token.trim() === "") {
                setError("Please log in to access the admin panel.");
                setLoading(false);
                return;
            }

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            let res;
            try {
                res = await fetch(API_ENDPOINTS.ADMIN.USERS, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                    credentials: "include",
                    cache: "no-store",
                    signal: controller.signal,
                });
                clearTimeout(timeoutId);
            } catch (fetchErr) {
                clearTimeout(timeoutId);
                if (fetchErr.name === "AbortError") {
                    setError("Request timeout: Server took too long to respond.");
                } else {
                    setError("Network error: Unable to connect to server.");
                }
                setLoading(false);
                return;
            }

            const rawText = await res.text();
            const trimmed = rawText ? rawText.trim() : "";
            if (!trimmed || trimmed === "undefined" || trimmed === "null") {
                setError("Empty response from server.");
                setLoading(false);
                return;
            }

            let data;
            try {
                data = JSON.parse(trimmed);
            } catch (e) {
                setError("Invalid response from server.");
                setLoading(false);
                return;
            }

            if (!res.ok) {
                if (res.status === 401 || res.status === 403) {
                    clearAuthSession();
                    setError("Session expired. Please log in again.");
                } else {
                    setError(data.message || `Failed to load users (${res.status})`);
                }
                setLoading(false);
                return;
            }

            setUsers(Array.isArray(data.users) ? data.users : Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e.message || "Failed to load users.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        const interval = setInterval(fetchUsers, 30 * 1000);
        return () => clearInterval(interval);
    }, [fetchUsers]);

    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;

    const filtered = users.filter(u => {
        const matchesSearch =
            !search ||
            (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
            (u.email || "").toLowerCase().includes(search.toLowerCase());

        if (!matchesSearch) return false;

        if (tab === "recent") {
            const joined = new Date(u.createdAt || u.joinedAt).getTime();
            return joined >= sevenDaysAgo;
        }
        if (tab === "subscribed") {
            return u.subscription && u.subscription !== "Free";
        }
        return true;
    });

    const recentCount = users.filter(u => {
        const joined = new Date(u.createdAt || u.joinedAt).getTime();
        return joined >= sevenDaysAgo;
    }).length;

    const subscribedCount = users.filter(u => u.subscription && u.subscription !== "Free").length;

    return (
        <div className="admin-container w-full max-w-none px-0 pb-[20px]">
            <AdminTopNav activeTab="dashboard" navPinned={navPinned} onTogglePin={() => setNavPinned(v => !v)} />

            <main className="admin-content">
                <div className="adash-container rounded-[14px] bg-bg p-[14px] shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
                    <div className="adash-header flex flex-wrap items-center justify-between gap-[10px]">
                        <h1 className="adash-title">Users</h1>
                        <div className="adash-header-right">
                            <div className="adash-search-wrap">
                                <i className="fi fi-rr-search adash-search-icon"></i>
                                <input
                                    className="adash-search"
                                    type="text"
                                    placeholder="Search users..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                            <button
                                className="adash-refresh-btn"
                                onClick={fetchUsers}
                                disabled={loading}
                                title="Refresh"
                            >
                                <i className={`fi fi-rr-rotate-right${loading ? " adash-spinning" : ""}`}></i>
                                Refresh
                            </button>
                        </div>
                    </div>

                    <div className="adash-tabs mt-[12px] flex flex-wrap gap-[8px]">
                        <button
                            className={`adash-tab${tab === "all" ? " adash-tab-active" : ""}`}
                            onClick={() => setTab("all")}
                        >
                            All Users
                            <span className="adash-tab-badge">{users.length}</span>
                        </button>
                        <button
                            className={`adash-tab${tab === "subscribed" ? " adash-tab-active" : ""}`}
                            onClick={() => setTab("subscribed")}
                        >
                            Subscribed
                            {subscribedCount > 0 && (
                                <span className="adash-tab-badge">{subscribedCount}</span>
                            )}
                        </button>
                        <button
                            className={`adash-tab${tab === "recent" ? " adash-tab-active" : ""}`}
                            onClick={() => setTab("recent")}
                        >
                            Recent
                            {recentCount > 0 && (
                                <span className="adash-tab-badge">{recentCount}</span>
                            )}
                        </button>
                    </div>

                    {error && (
                        <div className="adash-error">{error}</div>
                    )}

                    {loading && (
                        <div className="adash-loading">
                            <div className="adash-spinner"></div>
                            Loading users...
                        </div>
                    )}

                    {!loading && !error && (
                        <div className="adash-table-wrap mt-[12px] overflow-x-auto rounded-[10px] border border-slider">
                            <table className="adash-table">
                                <thead>
                                    <tr>
                                        <th className="adash-th adash-th-user">User</th>
                                        <th className="adash-th">Joined</th>
                                        <th className="adash-th">Subscription</th>
                                        <th className="adash-th adash-th-actions"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="adash-empty">
                                                {search ? "No users match your search." : "No users found."}
                                            </td>
                                        </tr>
                                    ) : (
                                        filtered.map((user, i) => (
                                            <tr key={user._id || user.id || i} className="adash-row">
                                                <td className="adash-td adash-td-user">
                                                    <div className="adash-user-cell">
                                                        <div className="adash-avatar-wrap">
                                                            <UserAvatar user={user} />
                                                            <span className={`adash-status-dot${user.isOnline ? " adash-dot-online" : " adash-dot-offline"}`}></span>
                                                        </div>
                                                        <div className="adash-user-info">
                                                            <span className="adash-user-name">{user.name || "—"}</span>
                                                            <span className="adash-user-email">{user.email || "—"}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="adash-td">
                                                    <span className="adash-joined">
                                                        {formatDate(user.createdAt || user.joinedAt)}
                                                    </span>
                                                </td>
                                                <td className="adash-td">
                                                    <div className="adash-sub-cell">
                                                        <span className={`adash-subscription adash-sub-${(user.subscription || "free").toLowerCase()}`}>
                                                            {user.subscription || "Free"}
                                                        </span>
                                                        {user.subscriptionExpiry && (
                                                            <span className="adash-sub-expiry">
                                                                Exp: {formatDate(user.subscriptionExpiry)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="adash-td adash-td-actions">
                                                    <button className="adash-action-btn" title="More options">
                                                        <i className="fi fi-rr-menu-dots-vertical"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
